import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/App";
import { 
  ArrowLeft, BookOpen, Users, UserCircle, ChevronRight, 
  CheckCircle, FileText, HelpCircle, Search, ExternalLink
} from "lucide-react";

// Client SOP Content
const ClientSOPContent = () => (
  <div className="prose prose-neutral max-w-none">
    <h1 className="text-2xl font-bold text-[#333333] mb-6">Client Program Guide</h1>
    
    {/* Table of Contents */}
    <Card className="mb-8 border-[#39FF14]/20 bg-green-50/50">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center">
          <BookOpen className="w-5 h-5 mr-2 text-[#39FF14]" />
          Quick Navigation
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid md:grid-cols-2 gap-2">
          {[
            { id: "getting-started", label: "Getting Started" },
            { id: "dashboard", label: "Your Dashboard" },
            { id: "my-cases", label: "My Cases" },
            { id: "business-planning", label: "Business Planning Tools" },
            { id: "brand-development", label: "Brand Development" },
            { id: "goals-actions", label: "Goal Setting & Action Plans" },
            { id: "time-management", label: "Time Management" },
            { id: "weekly-tracking", label: "Weekly Progress Tracking" },
            { id: "rapid-program", label: "R.A.P.I.D.™ Program" },
            { id: "workshops", label: "Workshops & Events" },
            { id: "community", label: "Community Features" },
            { id: "account", label: "Account Management" }
          ].map((item) => (
            <a 
              key={item.id}
              href={`#${item.id}`}
              className="flex items-center text-sm text-[#333333] hover:text-[#39FF14] transition-colors p-2 rounded hover:bg-white"
            >
              <ChevronRight className="w-4 h-4 mr-2 text-[#39FF14]" />
              {item.label}
            </a>
          ))}
        </div>
      </CardContent>
    </Card>

    {/* Section 1: Getting Started */}
    <section id="getting-started" className="mb-10">
      <h2 className="text-xl font-semibold text-[#333333] border-b border-neutral-200 pb-2 mb-4">
        1. Getting Started
      </h2>
      
      <h3 className="text-lg font-medium text-[#39FF14] mt-6 mb-3">1.1 Creating Your Account</h3>
      <ol className="list-decimal list-inside space-y-2 text-neutral-700">
        <li>Navigate to the My Biz Consulting registration page</li>
        <li>Enter your information: Full name, Email address, Password, Business name</li>
        <li>Click <strong>"Create Account"</strong></li>
        <li>You'll receive a confirmation email</li>
      </ol>

      <h3 className="text-lg font-medium text-[#39FF14] mt-6 mb-3">1.2 Awaiting Approval</h3>
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
        <p className="text-amber-800 text-sm">
          <strong>Note:</strong> After registration, your account enters "Pending Approval" status. 
          A consultant will review your application and you'll receive an email when approved.
        </p>
      </div>
      <ol className="list-decimal list-inside space-y-2 text-neutral-700">
        <li>Complete your initial onboarding questionnaire</li>
        <li>Your account enters <Badge variant="outline" className="mx-1">Pending Approval</Badge> status</li>
        <li>A consultant will review your application</li>
        <li>You'll receive an email when approved</li>
        <li>Once approved, you can access all dashboard features</li>
      </ol>

      <h3 className="text-lg font-medium text-[#39FF14] mt-6 mb-3">1.3 Logging In</h3>
      <ol className="list-decimal list-inside space-y-2 text-neutral-700">
        <li>Go to the My Biz Consulting login page</li>
        <li>Enter your email address and password</li>
        <li>Click <strong>"Sign In"</strong></li>
        <li>Or click <strong>"Continue with Google"</strong> for quick access</li>
      </ol>
    </section>

    {/* Section 2: Dashboard */}
    <section id="dashboard" className="mb-10">
      <h2 className="text-xl font-semibold text-[#333333] border-b border-neutral-200 pb-2 mb-4">
        2. Your Dashboard
      </h2>
      
      <h3 className="text-lg font-medium text-[#39FF14] mt-6 mb-3">2.1 Dashboard Overview</h3>
      <p className="text-neutral-700 mb-4">Your dashboard displays:</p>
      <ul className="list-disc list-inside space-y-2 text-neutral-700 ml-4">
        <li><strong>Welcome Message</strong>: Personalized greeting</li>
        <li><strong>Quick Actions</strong>: Common tasks</li>
        <li><strong>Progress Summary</strong>: Completion status of key sections</li>
        <li><strong>Upcoming Sessions</strong>: Scheduled meetings with your consultant</li>
        <li><strong>Recent Activity</strong>: Latest updates and notifications</li>
      </ul>

      <h3 className="text-lg font-medium text-[#39FF14] mt-6 mb-3">2.2 Navigation Menu</h3>
      <div className="grid md:grid-cols-2 gap-3">
        {[
          { icon: "🏠", label: "Dashboard", desc: "Main overview" },
          { icon: "👤", label: "My Profile", desc: "Ideal client & business info" },
          { icon: "💼", label: "My Cases", desc: "View your cases" },
          { icon: "🎯", label: "Vision & Goals", desc: "Business objectives" },
          { icon: "📋", label: "Action Plans", desc: "Tasks and projects" },
          { icon: "💰", label: "Sales Tracking", desc: "Revenue and pipeline" },
          { icon: "🎨", label: "Brand Studio", desc: "Brand guidelines & logo" },
          { icon: "⏰", label: "Time Budget", desc: "Time allocation" },
          { icon: "📝", label: "Weekly Log", desc: "Progress tracking" },
          { icon: "📊", label: "Reports", desc: "Custom report builder" }
        ].map((item, idx) => (
          <div key={idx} className="flex items-center p-3 bg-neutral-50 rounded-lg">
            <span className="text-xl mr-3">{item.icon}</span>
            <div>
              <p className="font-medium text-[#333333]">{item.label}</p>
              <p className="text-xs text-neutral-500">{item.desc}</p>
            </div>
          </div>
        ))}
      </div>
    </section>

    {/* Section 3: My Cases */}
    <section id="my-cases" className="mb-10">
      <h2 className="text-xl font-semibold text-[#333333] border-b border-neutral-200 pb-2 mb-4">
        3. My Cases
      </h2>
      
      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mb-4">
        <p className="text-emerald-800 text-sm">
          Cases are your consulting projects, support tickets, and strategy sessions. Use this feature to communicate with your consultant and track progress.
        </p>
      </div>

      <h3 className="text-lg font-medium text-[#39FF14] mt-6 mb-3">3.1 Viewing Your Cases</h3>
      <ol className="list-decimal list-inside space-y-2 text-neutral-700">
        <li>Click the <strong>"My Cases"</strong> card on your dashboard</li>
        <li>Or find the Cases section in your navigation</li>
        <li>View all your cases in a list with status indicators</li>
        <li>Use filters to sort by status, type, or priority</li>
      </ol>

      <h3 className="text-lg font-medium text-[#39FF14] mt-6 mb-3">3.2 Understanding Case Status</h3>
      <div className="grid md:grid-cols-3 gap-4 mt-4">
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h4 className="font-semibold text-blue-800 mb-2">Open</h4>
          <p className="text-sm text-blue-700">New case, not yet started</p>
        </div>
        <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
          <h4 className="font-semibold text-purple-800 mb-2">In Progress</h4>
          <p className="text-sm text-purple-700">Your consultant is actively working on it</p>
        </div>
        <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
          <h4 className="font-semibold text-gray-800 mb-2">Closed</h4>
          <p className="text-sm text-gray-700">Completed or resolved</p>
        </div>
      </div>

      <h3 className="text-lg font-medium text-[#39FF14] mt-6 mb-3">3.3 Communicating on a Case</h3>
      <ol className="list-decimal list-inside space-y-2 text-neutral-700">
        <li>Click on a case to open its details</li>
        <li>Go to the <strong>"Interactions"</strong> tab</li>
        <li>Read the conversation history</li>
        <li>Type your message in the input field</li>
        <li>Click <strong>Send</strong> to post your comment</li>
        <li>Your consultant will be notified via email</li>
      </ol>

      <h3 className="text-lg font-medium text-[#39FF14] mt-6 mb-3">3.4 Adding Documents</h3>
      <ol className="list-decimal list-inside space-y-2 text-neutral-700">
        <li>Open your case and go to <strong>"Documents"</strong> tab</li>
        <li>Click <strong>"Add Document"</strong></li>
        <li>Enter the document name</li>
        <li>Paste the URL (Google Drive, Dropbox, etc.)</li>
        <li>Add an optional description</li>
        <li>Click <strong>"Add Document"</strong></li>
      </ol>

      <h3 className="text-lg font-medium text-[#39FF14] mt-6 mb-3">3.5 Email Notifications</h3>
      <p className="text-neutral-700 mb-3">You'll receive email notifications when:</p>
      <ul className="list-disc list-inside space-y-2 text-neutral-700 ml-4">
        <li>A new case is created for you</li>
        <li>Your case status changes (e.g., moved to "In Progress" or "Closed")</li>
        <li>Your consultant adds a comment to your case</li>
      </ul>
    </section>

    {/* Section 4: Business Planning */}
    <section id="business-planning" className="mb-10">
      <h2 className="text-xl font-semibold text-[#333333] border-b border-neutral-200 pb-2 mb-4">
        4. Business Planning Tools
      </h2>
      
      <h3 className="text-lg font-medium text-[#39FF14] mt-6 mb-3">4.1 Creating Your Business Plan</h3>
      <ol className="list-decimal list-inside space-y-2 text-neutral-700">
        <li>Navigate to <strong>Business Plan</strong> in the sidebar</li>
        <li>Click <strong>"Generate Business Plan"</strong></li>
        <li>Complete the questionnaire: Business overview, Products/services, Target market, Revenue goals, Challenges</li>
        <li>Click <strong>"Generate with AI"</strong></li>
        <li>Review the generated plan sections</li>
        <li>Edit any section as needed</li>
        <li>Click <strong>"Save Plan"</strong></li>
      </ol>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
        <p className="text-blue-800 text-sm">
          <strong>Tip:</strong> The more detailed your questionnaire answers, the better your AI-generated business plan will be!
        </p>
      </div>
    </section>

    {/* Section 4: Brand Development */}
    <section id="brand-development" className="mb-10">
      <h2 className="text-xl font-semibold text-[#333333] border-b border-neutral-200 pb-2 mb-4">
        4. Brand Development
      </h2>
      
      <h3 className="text-lg font-medium text-[#39FF14] mt-6 mb-3">4.1 Setting Up Brand Guidelines</h3>
      <ol className="list-decimal list-inside space-y-2 text-neutral-700">
        <li>Navigate to <strong>Brand Studio</strong></li>
        <li>Click <strong>"Create Brand Guidelines"</strong></li>
        <li>Answer the brand questionnaire</li>
        <li>Click <strong>"Generate Guidelines"</strong></li>
        <li>Review AI-generated brand elements: Brand Promise, Value Proposition, Voice & Tone, Messaging Framework</li>
      </ol>

      <h3 className="text-lg font-medium text-[#39FF14] mt-6 mb-3">4.2 Logo Options</h3>
      <ul className="list-disc list-inside space-y-2 text-neutral-700 ml-4">
        <li><strong>Upload Your Logo</strong>: Use your existing logo (PNG, JPG, or SVG)</li>
        <li><strong>Generate with AI</strong>: Create a new logo using AI based on your brand description</li>
      </ul>
    </section>

    {/* Section 5: Goals & Actions */}
    <section id="goals-actions" className="mb-10">
      <h2 className="text-xl font-semibold text-[#333333] border-b border-neutral-200 pb-2 mb-4">
        5. Goal Setting & Action Plans
      </h2>
      
      <h3 className="text-lg font-medium text-[#39FF14] mt-6 mb-3">5.1 Defining Your Vision & Goals</h3>
      <ol className="list-decimal list-inside space-y-2 text-neutral-700">
        <li>Navigate to <strong>Vision & Goals</strong></li>
        <li>Complete each section:
          <ul className="list-disc list-inside ml-6 mt-2 space-y-1">
            <li><strong>Vision Statement</strong>: Your long-term business vision</li>
            <li><strong>1-Year Goals</strong>: 3-5 major goals for the next year</li>
            <li><strong>90-Day Goals</strong>: Quarterly objectives</li>
            <li><strong>Core Values</strong>: 3-5 guiding principles</li>
          </ul>
        </li>
        <li>Click <strong>"Save Goals"</strong></li>
      </ol>

      <h3 className="text-lg font-medium text-[#39FF14] mt-6 mb-3">5.2 Creating Action Plans</h3>
      <ol className="list-decimal list-inside space-y-2 text-neutral-700">
        <li>Navigate to <strong>Action Plans</strong></li>
        <li>Click <strong>"+ New Plan"</strong></li>
        <li>Enter plan details: Title, Description, Category, Due date</li>
        <li>Add tasks with priorities and due dates</li>
        <li>Click <strong>"Create Plan"</strong></li>
      </ol>
    </section>

    {/* Section 6: Time Management */}
    <section id="time-management" className="mb-10">
      <h2 className="text-xl font-semibold text-[#333333] border-b border-neutral-200 pb-2 mb-4">
        6. Time Management
      </h2>
      
      <h3 className="text-lg font-medium text-[#39FF14] mt-6 mb-3">6.1 Understanding Time Budget</h3>
      <p className="text-neutral-700 mb-4">
        The Time Budget tool helps you allocate your 168 weekly hours across work and personal categories.
      </p>

      <h3 className="text-lg font-medium text-[#39FF14] mt-6 mb-3">6.2 Setting Up Your Time Budget</h3>
      <ol className="list-decimal list-inside space-y-2 text-neutral-700">
        <li>Navigate to <strong>Time Budget</strong></li>
        <li>Allocate hours for work categories: Client Sessions, Preparation, Admin, Marketing, Learning</li>
        <li>Allocate hours for personal categories: Sleep, Family, Exercise, Personal Development</li>
        <li>Click <strong>"Save Budget"</strong></li>
        <li>Click <strong>"Get AI Tips"</strong> for personalized recommendations</li>
      </ol>
    </section>

    {/* Section 7: Weekly Tracking */}
    <section id="weekly-tracking" className="mb-10">
      <h2 className="text-xl font-semibold text-[#333333] border-b border-neutral-200 pb-2 mb-4">
        7. Weekly Progress Tracking
      </h2>
      
      <h3 className="text-lg font-medium text-[#39FF14] mt-6 mb-3">7.1 Creating Weekly Logs</h3>
      <ol className="list-decimal list-inside space-y-2 text-neutral-700">
        <li>Navigate to <strong>Weekly Log</strong></li>
        <li>Click <strong>"+ New Entry"</strong></li>
        <li>Complete the log:
          <ul className="list-disc list-inside ml-6 mt-2 space-y-1">
            <li><strong>Accomplishments</strong>: 3-5 key wins this week</li>
            <li><strong>Challenges</strong>: Obstacles and lessons learned</li>
            <li><strong>Next Week Focus</strong>: Upcoming priorities</li>
            <li><strong>Metrics</strong>: Revenue, leads, projects completed</li>
          </ul>
        </li>
        <li>Click <strong>"Save Log"</strong></li>
      </ol>

      <div className="bg-green-50 border border-green-200 rounded-lg p-4 mt-4">
        <p className="text-green-800 text-sm">
          <strong>Best Practice:</strong> Set aside 30 minutes at the end of each week for logging. Consistent tracking drives accountability!
        </p>
      </div>
    </section>

    {/* Section 8: R.A.P.I.D. Program */}
    <section id="rapid-program" className="mb-10">
      <h2 className="text-xl font-semibold text-[#333333] border-b border-neutral-200 pb-2 mb-4">
        8. R.A.P.I.D.™ Program
      </h2>
      
      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-4">
        <p className="text-purple-800 text-sm">
          <strong>R.A.P.I.D.™</strong> = Review → Analyze → Plan → Implement → Develop
        </p>
        <p className="text-purple-700 text-sm mt-2">
          Available for clients enrolled in the R.A.P.I.D.™ tier - a premium 3-session business recalibration framework.
        </p>
      </div>

      <h3 className="text-lg font-medium text-[#39FF14] mt-6 mb-3">The Three Sessions</h3>
      <div className="space-y-4">
        <div className="p-4 bg-neutral-50 rounded-lg border-l-4 border-emerald-500">
          <h4 className="font-semibold text-[#333333]">Session 1: Review & Analyze</h4>
          <p className="text-sm text-neutral-600 mt-1">Identify internal constraints and assumptions shaping your decisions.</p>
        </div>
        <div className="p-4 bg-neutral-50 rounded-lg border-l-4 border-blue-500">
          <h4 className="font-semibold text-[#333333]">Session 2: Plan & Implement</h4>
          <p className="text-sm text-neutral-600 mt-1">Recalibrate decision-making and install leadership standards.</p>
        </div>
        <div className="p-4 bg-neutral-50 rounded-lg border-l-4 border-orange-500">
          <h4 className="font-semibold text-[#333333]">Session 3: Develop & Integrate</h4>
          <p className="text-sm text-neutral-600 mt-1">Create your 30-60-90 day momentum plan for sustained execution.</p>
        </div>
      </div>
    </section>

    {/* Section 9: Reports */}
    <section id="reports" className="mb-10">
      <h2 className="text-xl font-semibold text-[#333333] border-b border-neutral-200 pb-2 mb-4">
        9. Reports & Documentation
      </h2>
      
      <h3 className="text-lg font-medium text-[#39FF14] mt-6 mb-3">9.1 Creating Custom Reports</h3>
      <ol className="list-decimal list-inside space-y-2 text-neutral-700">
        <li>Go to <strong>Custom Report</strong> in your menu</li>
        <li>Select sections to include:
          <ul className="list-disc list-inside ml-6 mt-2 space-y-1 text-sm">
            <li>Ideal Client Profile</li>
            <li>Vision & Goals</li>
            <li>Action Plans</li>
            <li>Sales Performance</li>
            <li>Brand Guidelines</li>
            <li>Time Budget</li>
            <li>Weekly Progress</li>
            <li>Projects</li>
          </ul>
        </li>
        <li>Enter a custom report title</li>
        <li>Click <strong>"Preview"</strong> to see the report</li>
        <li>Click <strong>"Generate PDF"</strong> to download</li>
      </ol>

      <h3 className="text-lg font-medium text-[#39FF14] mt-6 mb-3">9.2 Report Features</h3>
      <ul className="list-disc list-inside space-y-2 text-neutral-700 ml-4">
        <li>Company logo on cover page</li>
        <li>Professional formatting with branded colors</li>
        <li>Page numbers and section dividers</li>
        <li>Data tables and summaries</li>
      </ul>
    </section>

    {/* Section 10: Account */}
    <section id="account" className="mb-10">
      <h2 className="text-xl font-semibold text-[#333333] border-b border-neutral-200 pb-2 mb-4">
        10. Account Management
      </h2>
      
      <h3 className="text-lg font-medium text-[#39FF14] mt-6 mb-3">10.1 Requesting a Plan Upgrade</h3>
      <ol className="list-decimal list-inside space-y-2 text-neutral-700">
        <li>From your dashboard, click <strong>"Upgrade Plan"</strong></li>
        <li>Select your desired tier</li>
        <li>Add a message explaining your needs (optional)</li>
        <li>Click <strong>"Submit Request"</strong></li>
        <li>Your consultant will review and respond via email</li>
      </ol>

      <h3 className="text-lg font-medium text-[#39FF14] mt-6 mb-3">10.2 Updating Your Profile</h3>
      <ol className="list-decimal list-inside space-y-2 text-neutral-700">
        <li>Click your name/avatar in the header</li>
        <li>Select <strong>"Profile Settings"</strong></li>
        <li>Update your information</li>
        <li>Click <strong>"Save Changes"</strong></li>
      </ol>
    </section>

    {/* Tips for Success */}
    <section className="mb-6">
      <Card className="border-[#39FF14] bg-gradient-to-br from-green-50 to-emerald-50">
        <CardHeader>
          <CardTitle className="text-lg text-[#333333]">💡 Tips for Success</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-2 text-sm text-neutral-700">
            <li className="flex items-start">
              <CheckCircle className="w-4 h-4 mr-2 text-[#39FF14] mt-0.5 flex-shrink-0" />
              <span><strong>Complete your profile fully</strong> - More data = better AI insights</span>
            </li>
            <li className="flex items-start">
              <CheckCircle className="w-4 h-4 mr-2 text-[#39FF14] mt-0.5 flex-shrink-0" />
              <span><strong>Log weekly</strong> - Consistent tracking drives accountability</span>
            </li>
            <li className="flex items-start">
              <CheckCircle className="w-4 h-4 mr-2 text-[#39FF14] mt-0.5 flex-shrink-0" />
              <span><strong>Use AI tools</strong> - They're included in your plan</span>
            </li>
            <li className="flex items-start">
              <CheckCircle className="w-4 h-4 mr-2 text-[#39FF14] mt-0.5 flex-shrink-0" />
              <span><strong>Review regularly</strong> - Check your dashboard weekly</span>
            </li>
            <li className="flex items-start">
              <CheckCircle className="w-4 h-4 mr-2 text-[#39FF14] mt-0.5 flex-shrink-0" />
              <span><strong>Communicate</strong> - Keep your consultant informed</span>
            </li>
          </ul>
        </CardContent>
      </Card>
    </section>
  </div>
);

// Consultant SOP Content
const ConsultantSOPContent = () => (
  <div className="prose prose-neutral max-w-none">
    <h1 className="text-2xl font-bold text-[#333333] mb-6">Consultant Operations Guide</h1>
    
    {/* Table of Contents */}
    <Card className="mb-8 border-purple-200 bg-purple-50/50">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center">
          <BookOpen className="w-5 h-5 mr-2 text-purple-600" />
          Quick Navigation
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid md:grid-cols-2 gap-2">
          {[
            { id: "c-getting-started", label: "Getting Started" },
            { id: "c-client-management", label: "Client Management" },
            { id: "c-case-management", label: "Case Management & CRM" },
            { id: "c-community", label: "Community & Invitations" },
            { id: "c-rapid-management", label: "R.A.P.I.D.™ Management" },
            { id: "c-workshops", label: "Events & Workshops" },
            { id: "c-bulletin", label: "Bulletin Board" },
            { id: "c-newsletters", label: "Newsletters" },
            { id: "c-ai-tools", label: "AI Tools & Resources" },
            { id: "c-reports", label: "Reports & Sponsor Reports" }
          ].map((item) => (
            <a 
              key={item.id}
              href={`#${item.id}`}
              className="flex items-center text-sm text-[#333333] hover:text-purple-600 transition-colors p-2 rounded hover:bg-white"
            >
              <ChevronRight className="w-4 h-4 mr-2 text-purple-600" />
              {item.label}
            </a>
          ))}
        </div>
      </CardContent>
    </Card>

    {/* Section 1: Getting Started */}
    <section id="c-getting-started" className="mb-10">
      <h2 className="text-xl font-semibold text-[#333333] border-b border-neutral-200 pb-2 mb-4">
        1. Getting Started
      </h2>
      
      <h3 className="text-lg font-medium text-purple-600 mt-6 mb-3">1.1 Logging In</h3>
      <ol className="list-decimal list-inside space-y-2 text-neutral-700">
        <li>Navigate to the My Biz Consulting dashboard URL</li>
        <li>Enter your consultant email address and password</li>
        <li>Click <strong>"Sign In"</strong></li>
        <li>Or use <strong>"Continue with Google"</strong> for OAuth login</li>
      </ol>

      <h3 className="text-lg font-medium text-purple-600 mt-6 mb-3">1.2 Dashboard Overview</h3>
      <p className="text-neutral-700 mb-4">Upon login, your Consultant Dashboard displays:</p>
      <ul className="list-disc list-inside space-y-2 text-neutral-700 ml-4">
        <li><strong>Quick Stats</strong>: Total clients, pending approvals, upcoming meetings</li>
        <li><strong>Client List</strong>: All your assigned clients</li>
        <li><strong>Pending Approvals</strong>: New clients awaiting your approval</li>
        <li><strong>Upgrade Requests</strong>: Clients requesting plan upgrades</li>
        <li><strong>Recent Activity</strong>: Latest client interactions</li>
      </ul>
      
      <h3 className="text-lg font-medium text-purple-600 mt-6 mb-3">1.3 Sidebar Navigation</h3>
      <ul className="list-disc list-inside space-y-2 text-neutral-700 ml-4">
        <li><strong>Dashboard</strong>: Main overview</li>
        <li><strong>Clients</strong>: Client management</li>
        <li><strong>Bulletin Board</strong>: Community posts</li>
        <li><strong>Community</strong>: Member management & invitations</li>
        <li><strong>Newsletters</strong>: Email campaigns</li>
        <li><strong>Community View</strong>: Moderate posts & comments</li>
        <li><strong>Events</strong>: Workshop management</li>
        <li><strong>Cases / CRM</strong>: Case management system</li>
        <li><strong>Analytics</strong>: Reports & insights</li>
      </ul>
    </section>

    {/* Section 2: Client Management */}
    <section id="c-client-management" className="mb-10">
      <h2 className="text-xl font-semibold text-[#333333] border-b border-neutral-200 pb-2 mb-4">
        2. Client Management
      </h2>
      
      <h3 className="text-lg font-medium text-purple-600 mt-6 mb-3">2.1 Approving New Clients</h3>
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
        <p className="text-blue-800 text-sm">
          When a new client registers, they appear in the <strong>"Awaiting Approval"</strong> section.
        </p>
      </div>
      <ol className="list-decimal list-inside space-y-2 text-neutral-700">
        <li>Locate the <strong>"Awaiting Approval"</strong> section on your dashboard</li>
        <li>Review the client's information (name, email, business name)</li>
        <li>Click the <strong>"Approve as..."</strong> dropdown</li>
        <li>Select the appropriate program tier:
          <ul className="list-disc list-inside ml-6 mt-2 space-y-1 text-sm">
            <li><strong>Basic</strong> - Standard client access</li>
            <li><strong>R.A.P.I.D.™</strong> - Premium business recalibration</li>
            <li><strong>Strategic Foundation</strong> - Strategic planning tier</li>
            <li><strong>Growth & Optimization</strong> - Growth-focused consulting</li>
            <li><strong>Scale & Leadership</strong> - Enterprise-level support</li>
          </ul>
        </li>
        <li>The client will receive an email notification of their approval</li>
      </ol>

      <h3 className="text-lg font-medium text-purple-600 mt-6 mb-3">2.2 Viewing Client Details</h3>
      <ol className="list-decimal list-inside space-y-2 text-neutral-700">
        <li>Click on any client name in your client list</li>
        <li>View comprehensive client information:
          <ul className="list-disc list-inside ml-6 mt-2 space-y-1 text-sm">
            <li>Contact details and business information</li>
            <li>Intake form responses</li>
            <li>Weekly logs and progress</li>
            <li>Active projects and tasks</li>
            <li>AI-generated content (business plans, brand guidelines)</li>
            <li>R.A.P.I.D.™ enrollment status</li>
          </ul>
        </li>
      </ol>

      <h3 className="text-lg font-medium text-purple-600 mt-6 mb-3">2.3 Changing Client Tier</h3>
      <ol className="list-decimal list-inside space-y-2 text-neutral-700">
        <li>Navigate to the client list</li>
        <li>Find the client's row</li>
        <li>Click the <strong>tier dropdown</strong> next to their name</li>
        <li>Select the new tier - changes take effect immediately</li>
      </ol>

      <h3 className="text-lg font-medium text-purple-600 mt-6 mb-3">2.4 Suspending/Restoring Client Access</h3>
      <div className="grid md:grid-cols-2 gap-4 mt-4">
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <h4 className="font-semibold text-red-800 mb-2">Suspending</h4>
          <ol className="list-decimal list-inside text-sm text-red-700 space-y-1">
            <li>Click on the client</li>
            <li>Click "Suspend" button</li>
            <li>Confirm the suspension</li>
          </ol>
        </div>
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
          <h4 className="font-semibold text-green-800 mb-2">Restoring</h4>
          <ol className="list-decimal list-inside text-sm text-green-700 space-y-1">
            <li>Find suspended client</li>
            <li>Click "Restore" button</li>
            <li>Access is immediately restored</li>
          </ol>
        </div>
      </div>

      <h3 className="text-lg font-medium text-purple-600 mt-6 mb-3">2.5 Processing Upgrade Requests</h3>
      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-4">
        <p className="text-purple-800 text-sm">
          Upgrade requests appear in the purple <strong>"Upgrade Requests"</strong> section on your dashboard.
        </p>
      </div>
      <ol className="list-decimal list-inside space-y-2 text-neutral-700">
        <li>Review the upgrade request details (client name, current tier, requested tier, message)</li>
        <li>Click <strong>"Approve"</strong> to approve - client tier is automatically updated</li>
        <li>Or click <strong>"Decline"</strong> to reject the request</li>
        <li>Client receives notification of your decision</li>
      </ol>
    </section>

    {/* Section 3: Case Management & CRM */}
    <section id="c-case-management" className="mb-10">
      <h2 className="text-xl font-semibold text-[#333333] border-b border-neutral-200 pb-2 mb-4">
        3. Case Management & CRM
      </h2>
      
      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mb-4">
        <p className="text-emerald-800 text-sm">
          <strong>Admin Note:</strong> The admin account (jamilah@mybizconsulting.net) has full system oversight and can view/manage all cases across all consultants.
        </p>
      </div>

      <h3 className="text-lg font-medium text-purple-600 mt-6 mb-3">3.1 Accessing Case Management</h3>
      <ol className="list-decimal list-inside space-y-2 text-neutral-700">
        <li>Click <strong>"Cases / CRM"</strong> in the sidebar</li>
        <li>The Case Management dialog opens showing all your cases</li>
        <li>View stats at the top: Open Cases, In Progress, High Priority, Closed</li>
      </ol>

      <h3 className="text-lg font-medium text-purple-600 mt-6 mb-3">3.2 Creating a New Case</h3>
      <ol className="list-decimal list-inside space-y-2 text-neutral-700">
        <li>Click the green <strong>"New Case"</strong> button</li>
        <li>Fill in the case details:
          <ul className="list-disc list-inside ml-4 mt-2 space-y-1">
            <li><strong>Title</strong>: Brief, descriptive case name</li>
            <li><strong>Description</strong>: Detailed information about the case</li>
            <li><strong>Case Type</strong>: Consulting Project, Support Ticket, or Strategy Session</li>
            <li><strong>Priority</strong>: High, Medium, or Low</li>
            <li><strong>Client</strong>: Select the client this case is for</li>
          </ul>
        </li>
        <li>Click <strong>"Create Case"</strong></li>
        <li>The client receives an email notification about the new case</li>
      </ol>

      <h3 className="text-lg font-medium text-purple-600 mt-6 mb-3">3.3 Case Types Explained</h3>
      <div className="grid md:grid-cols-3 gap-4 mt-4">
        <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
          <h4 className="font-semibold text-emerald-800 mb-2">Consulting Project</h4>
          <p className="text-sm text-emerald-700">Ongoing consulting engagements, business development projects, strategic initiatives</p>
        </div>
        <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
          <h4 className="font-semibold text-orange-800 mb-2">Support Ticket</h4>
          <p className="text-sm text-orange-700">Quick issues, questions, technical support, troubleshooting requests</p>
        </div>
        <div className="p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
          <h4 className="font-semibold text-indigo-800 mb-2">Strategy Session</h4>
          <p className="text-sm text-indigo-700">One-time or scheduled strategy discussions, planning meetings, reviews</p>
        </div>
      </div>

      <h3 className="text-lg font-medium text-purple-600 mt-6 mb-3">3.4 Managing Case Status</h3>
      <ol className="list-decimal list-inside space-y-2 text-neutral-700">
        <li>Click on a case to open its details</li>
        <li>Use the <strong>Status dropdown</strong> in the top-right to change status:
          <ul className="list-disc list-inside ml-4 mt-2 space-y-1">
            <li><strong>Open</strong>: New case, not yet started</li>
            <li><strong>In Progress</strong>: Actively being worked on</li>
            <li><strong>Closed</strong>: Completed or resolved</li>
          </ul>
        </li>
        <li>Client receives email notification when status changes</li>
      </ol>

      <h3 className="text-lg font-medium text-purple-600 mt-6 mb-3">3.5 Case Interactions</h3>
      <ol className="list-decimal list-inside space-y-2 text-neutral-700">
        <li>Open a case and go to the <strong>"Interactions"</strong> tab</li>
        <li>View the full conversation history</li>
        <li>Type a message and click <strong>Send</strong> to add a comment</li>
        <li>System automatically logs status changes and document additions</li>
        <li>Both consultant and client can add interactions</li>
        <li>Email notifications are sent when new comments are added</li>
      </ol>

      <h3 className="text-lg font-medium text-purple-600 mt-6 mb-3">3.6 Document Management</h3>
      <ol className="list-decimal list-inside space-y-2 text-neutral-700">
        <li>Open a case and go to the <strong>"Documents"</strong> tab</li>
        <li>Click <strong>"Add Document"</strong></li>
        <li>Enter the document name and URL (Google Drive, Dropbox, or any file link)</li>
        <li>Add an optional description</li>
        <li>Click <strong>"Add Document"</strong> to attach</li>
        <li>Documents can be deleted by clicking the trash icon</li>
      </ol>

      <h3 className="text-lg font-medium text-purple-600 mt-6 mb-3">3.7 Filtering & Searching Cases</h3>
      <ul className="list-disc list-inside space-y-2 text-neutral-700 ml-4">
        <li>Use the <strong>Search bar</strong> to find cases by title, client name, or description</li>
        <li><strong>Status filter</strong>: All, Open, In Progress, Closed</li>
        <li><strong>Type filter</strong>: All Types, Consulting, Support, Strategy</li>
        <li><strong>Priority filter</strong>: All, High, Medium, Low</li>
      </ul>
    </section>

    {/* Section 4: Community & Invitations */}
    <section id="c-community" className="mb-10">
      <h2 className="text-xl font-semibold text-[#333333] border-b border-neutral-200 pb-2 mb-4">
        4. Community & Invitations
      </h2>

      <h3 className="text-lg font-medium text-purple-600 mt-6 mb-3">4.1 Inviting New Users</h3>
      <ol className="list-decimal list-inside space-y-2 text-neutral-700">
        <li>Go to <strong>Community</strong> tab in sidebar</li>
        <li>Click <strong>"Invite User"</strong></li>
        <li>Choose invitation method:
          <ul className="list-disc list-inside ml-4 mt-2 space-y-1">
            <li><strong>Email Invitation</strong>: Send invite link to email</li>
            <li><strong>Create Account Directly</strong>: Create account with temporary password</li>
          </ul>
        </li>
        <li>Enter user details (email, name)</li>
        <li>Select role: Community Member or Client</li>
        <li>For clients, select their tier</li>
        <li>Click <strong>"Send Invite"</strong> or <strong>"Create Account"</strong></li>
      </ol>

      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 my-4">
        <p className="text-amber-800 text-sm">
          <strong>Important:</strong> When creating accounts directly, a dialog will display the temporary password. Copy and share it securely with the user.
        </p>
      </div>

      <h3 className="text-lg font-medium text-purple-600 mt-6 mb-3">4.2 Managing Pending Invitations</h3>
      <ul className="list-disc list-inside space-y-2 text-neutral-700 ml-4">
        <li>View all pending invitations in the Community tab</li>
        <li><strong>Resend</strong>: Click to resend invitation email</li>
        <li><strong>Cancel</strong>: Click to cancel pending invitation</li>
      </ul>

      <h3 className="text-lg font-medium text-purple-600 mt-6 mb-3">4.3 Upgrading Community Members</h3>
      <ol className="list-decimal list-inside space-y-2 text-neutral-700">
        <li>Find the community member in the list</li>
        <li>Click <strong>"Upgrade to Client"</strong></li>
        <li>Select the client tier</li>
        <li>Confirm upgrade - user receives email notification</li>
      </ol>
    </section>

    {/* Section 5: R.A.P.I.D. Management */}
    <section id="c-rapid-management" className="mb-10">
      <h2 className="text-xl font-semibold text-[#333333] border-b border-neutral-200 pb-2 mb-4">
        5. R.A.P.I.D.™ Program Management
      </h2>
      
      <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-lg p-4 mb-4">
        <p className="font-semibold text-purple-800">R.A.P.I.D.™ = Review → Analyze → Plan → Implement → Develop</p>
        <p className="text-purple-700 text-sm mt-2">
          A premium 3-session business recalibration framework designed to remove constraints and align decisions with growth.
        </p>
      </div>

      <h3 className="text-lg font-medium text-purple-600 mt-6 mb-3">5.1 Enrolling Clients</h3>
      <ol className="list-decimal list-inside space-y-2 text-neutral-700">
        <li>When approving a new client, select <strong>"R.A.P.I.D.™"</strong> as their tier</li>
        <li>Or change an existing client's tier to <strong>"R.A.P.I.D.™"</strong></li>
        <li>The system automatically creates their enrollment</li>
      </ol>

      <h3 className="text-lg font-medium text-purple-600 mt-6 mb-3">3.2 Session Structure</h3>
      <div className="space-y-4">
        <div className="p-4 bg-neutral-50 rounded-lg border-l-4 border-emerald-500">
          <h4 className="font-semibold text-[#333333]">Session 1: Review & Analyze</h4>
          <p className="text-sm text-neutral-600 mt-1">Client identifies internal constraints and assumptions. You review and provide feedback.</p>
        </div>
        <div className="p-4 bg-neutral-50 rounded-lg border-l-4 border-blue-500">
          <h4 className="font-semibold text-[#333333]">Session 2: Plan & Implement</h4>
          <p className="text-sm text-neutral-600 mt-1">Recalibrate decision-making, install leadership standards, define decision filters.</p>
        </div>
        <div className="p-4 bg-neutral-50 rounded-lg border-l-4 border-orange-500">
          <h4 className="font-semibold text-[#333333]">Session 3: Develop & Integrate</h4>
          <p className="text-sm text-neutral-600 mt-1">Create 30-60-90 day momentum plan, establish tracking mechanisms.</p>
        </div>
      </div>
    </section>

    {/* Section 4: Workshops & Meetings */}
    <section id="c-workshops" className="mb-10">
      <h2 className="text-xl font-semibold text-[#333333] border-b border-neutral-200 pb-2 mb-4">
        4. Workshops & Meetings
      </h2>
      
      <h3 className="text-lg font-medium text-purple-600 mt-6 mb-3">4.1 Creating a Workshop</h3>
      <ol className="list-decimal list-inside space-y-2 text-neutral-700">
        <li>Click <strong>"+ Workshop"</strong> button on dashboard</li>
        <li>Fill in: Title, Description, Date/time, Duration, Type</li>
        <li>Click <strong>"Create Workshop"</strong></li>
      </ol>

      <h3 className="text-lg font-medium text-purple-600 mt-6 mb-3">4.2 Scheduling Client Meetings</h3>
      <ol className="list-decimal list-inside space-y-2 text-neutral-700">
        <li>Click <strong>"+ Meeting"</strong> button</li>
        <li>Select the client from dropdown</li>
        <li>Enter meeting details and schedule</li>
        <li>Client receives notification</li>
      </ol>

      <h3 className="text-lg font-medium text-purple-600 mt-6 mb-3">4.3 Setting Up Meeting Links</h3>
      <ol className="list-decimal list-inside space-y-2 text-neutral-700">
        <li>Go to <strong>Settings</strong></li>
        <li>Enter your video meeting link (Zoom, Google Meet)</li>
        <li>Enter your calendar booking link (Calendly)</li>
        <li>Links are automatically shared with clients</li>
      </ol>
    </section>

    {/* Section 6: Events & Workshops */}
    <section id="c-workshops" className="mb-10">
      <h2 className="text-xl font-semibold text-[#333333] border-b border-neutral-200 pb-2 mb-4">
        6. Events & Workshop Management
      </h2>

      <h3 className="text-lg font-medium text-purple-600 mt-6 mb-3">6.1 Accessing Events</h3>
      <ol className="list-decimal list-inside space-y-2 text-neutral-700">
        <li>Click <strong>"Events"</strong> in the sidebar</li>
        <li>View all events in list or create new ones</li>
      </ol>

      <h3 className="text-lg font-medium text-purple-600 mt-6 mb-3">6.2 Creating an Event</h3>
      <ol className="list-decimal list-inside space-y-2 text-neutral-700">
        <li>Click <strong>"Create Event"</strong> button</li>
        <li>Fill in event details:
          <ul className="list-disc list-inside ml-4 mt-2 space-y-1">
            <li><strong>Title</strong>: Event name</li>
            <li><strong>Description</strong>: Event details</li>
            <li><strong>Date & Time</strong>: When it occurs</li>
            <li><strong>Duration</strong>: Length in minutes</li>
            <li><strong>Location</strong>: Physical address or "Online"</li>
            <li><strong>Meeting Link</strong>: Zoom/Google Meet URL (optional)</li>
            <li><strong>Payment Link</strong>: Stripe/payment URL for paid events (optional)</li>
            <li><strong>Price</strong>: Event cost (0 for free)</li>
            <li><strong>Max Attendees</strong>: Capacity limit</li>
          </ul>
        </li>
        <li>Click <strong>"Save"</strong> to create</li>
      </ol>

      <h3 className="text-lg font-medium text-purple-600 mt-6 mb-3">6.3 Managing Registrations</h3>
      <ol className="list-decimal list-inside space-y-2 text-neutral-700">
        <li>Click on an event to view details</li>
        <li>Go to <strong>"Registrations"</strong> tab</li>
        <li>View list of registered attendees</li>
        <li>Remove attendees if needed by clicking the X icon</li>
      </ol>

      <h3 className="text-lg font-medium text-purple-600 mt-6 mb-3">6.4 Sending Reminders</h3>
      <ol className="list-decimal list-inside space-y-2 text-neutral-700">
        <li>Open the event details</li>
        <li>Click <strong>"Send Reminder"</strong></li>
        <li>Add an optional custom message</li>
        <li>All registered attendees receive an email reminder</li>
      </ol>
    </section>

    {/* Section 7: Bulletin Board */}
    <section id="c-bulletin" className="mb-10">
      <h2 className="text-xl font-semibold text-[#333333] border-b border-neutral-200 pb-2 mb-4">
        7. Bulletin Board
      </h2>

      <h3 className="text-lg font-medium text-purple-600 mt-6 mb-3">7.1 Creating Posts</h3>
      <ol className="list-decimal list-inside space-y-2 text-neutral-700">
        <li>Click <strong>"Bulletin Board"</strong> in sidebar</li>
        <li>Click <strong>"New Post"</strong></li>
        <li>Enter title and content</li>
        <li>Select category: Announcement, Tip, News, etc.</li>
        <li>Optionally add links or attachments</li>
        <li>Click <strong>"Publish"</strong> or <strong>"Schedule"</strong></li>
      </ol>

      <h3 className="text-lg font-medium text-purple-600 mt-6 mb-3">7.2 AI-Generated Posts</h3>
      <ol className="list-decimal list-inside space-y-2 text-neutral-700">
        <li>Click <strong>"Generate with AI"</strong></li>
        <li>Select content category (tip, news, motivation, resource)</li>
        <li>AI generates title and content</li>
        <li>Review and edit as needed</li>
        <li>Publish or schedule</li>
      </ol>

      <h3 className="text-lg font-medium text-purple-600 mt-6 mb-3">7.3 Scheduling Posts</h3>
      <ol className="list-decimal list-inside space-y-2 text-neutral-700">
        <li>Create your post</li>
        <li>Instead of "Publish", click <strong>"Schedule"</strong></li>
        <li>Select date and time</li>
        <li>Post will automatically publish at scheduled time</li>
      </ol>

      <h3 className="text-lg font-medium text-purple-600 mt-6 mb-3">7.4 Auto-Bulletin Settings</h3>
      <ol className="list-decimal list-inside space-y-2 text-neutral-700">
        <li>Click <strong>"Auto-Bulletin Settings"</strong></li>
        <li>Enable automatic daily posts</li>
        <li>Set time (default: 8:00 AM)</li>
        <li>Select days of the week</li>
        <li>Choose content categories to rotate</li>
        <li>AI will automatically generate and publish posts</li>
      </ol>

      <h3 className="text-lg font-medium text-purple-600 mt-6 mb-3">7.5 Moderating Comments</h3>
      <ol className="list-decimal list-inside space-y-2 text-neutral-700">
        <li>Go to <strong>"Community View"</strong> in sidebar</li>
        <li>View pending comments awaiting approval</li>
        <li>Click <strong>"Approve"</strong> or <strong>"Reject"</strong></li>
        <li>Approved comments appear on the post</li>
      </ol>
    </section>

    {/* Section 8: Newsletters */}
    <section id="c-newsletters" className="mb-10">
      <h2 className="text-xl font-semibold text-[#333333] border-b border-neutral-200 pb-2 mb-4">
        8. Newsletters
      </h2>

      <h3 className="text-lg font-medium text-purple-600 mt-6 mb-3">8.1 Creating a Newsletter</h3>
      <ol className="list-decimal list-inside space-y-2 text-neutral-700">
        <li>Click <strong>"Newsletters"</strong> in sidebar</li>
        <li>Click <strong>"Generate Newsletter"</strong></li>
        <li>Enter focus topics (optional)</li>
        <li>Check "Include upcoming workshops" if desired</li>
        <li>AI generates newsletter content</li>
        <li>Review and edit the generated content</li>
      </ol>

      <h3 className="text-lg font-medium text-purple-600 mt-6 mb-3">8.2 Sending Newsletters</h3>
      <ol className="list-decimal list-inside space-y-2 text-neutral-700">
        <li>Review the newsletter content and subject line</li>
        <li>Click <strong>"Send Newsletter"</strong></li>
        <li>Newsletter is sent to all clients and community members</li>
        <li>View send history in the "Newsletter History" section</li>
      </ol>

      <h3 className="text-lg font-medium text-purple-600 mt-6 mb-3">8.3 Auto-Newsletter Settings</h3>
      <ol className="list-decimal list-inside space-y-2 text-neutral-700">
        <li>Click <strong>"Newsletter Settings"</strong></li>
        <li>Enable automatic weekly newsletters</li>
        <li>Select send day (e.g., Monday)</li>
        <li>Set send time</li>
        <li>Configure default topics</li>
      </ol>
    </section>

    {/* Section 9: AI Tools */}
    <section id="c-ai-tools" className="mb-10">
      <h2 className="text-xl font-semibold text-[#333333] border-b border-neutral-200 pb-2 mb-4">
        9. AI Tools & Resources
      </h2>
      
      <h3 className="text-lg font-medium text-purple-600 mt-6 mb-3">9.1 Reviewing Client AI Content</h3>
      <p className="text-neutral-700 mb-3">Access client-generated AI content from their profile:</p>
      <ul className="list-disc list-inside space-y-2 text-neutral-700 ml-4">
        <li><strong>Business Plan</strong>: Executive summary, market analysis, projections</li>
        <li><strong>Brand Guidelines</strong>: Brand promise, voice, visual identity</li>
        <li><strong>SOPs</strong>: Standard operating procedures and processes</li>
      </ul>

      <h3 className="text-lg font-medium text-purple-600 mt-6 mb-3">9.2 Adding Consultant Notes</h3>
      <ol className="list-decimal list-inside space-y-2 text-neutral-700">
        <li>Navigate to client details</li>
        <li>Scroll to <strong>"Case Notes"</strong> section</li>
        <li>Click <strong>"Add Note"</strong></li>
        <li>Mark as internal or client-facing</li>
        <li>Click <strong>"Save"</strong></li>
      </ol>
    </section>

    {/* Section 10: Reports & Analytics */}
    <section id="c-reports" className="mb-10">
      <h2 className="text-xl font-semibold text-[#333333] border-b border-neutral-200 pb-2 mb-4">
        10. Reports & Analytics
      </h2>
      
      <h3 className="text-lg font-medium text-purple-600 mt-6 mb-3">10.1 Analytics Dashboard</h3>
      <p className="text-neutral-700 mb-3">Navigate to Analytics to view:</p>
      <ul className="list-disc list-inside space-y-2 text-neutral-700 ml-4">
        <li>Total clients and growth trends</li>
        <li>Project completion rates</li>
        <li>Client tier distribution</li>
        <li>R.A.P.I.D.™ enrollment stats</li>
        <li>Weekly log engagement</li>
        <li>Top engaged clients</li>
      </ul>

      <h3 className="text-lg font-medium text-purple-600 mt-6 mb-3">10.2 Custom Reports</h3>
      <ol className="list-decimal list-inside space-y-2 text-neutral-700">
        <li>Navigate to client details or <strong>Custom Report</strong></li>
        <li>Select sections to include (8 available)</li>
        <li>Enter custom report title</li>
        <li>Click <strong>"Preview"</strong> then <strong>"Generate PDF"</strong></li>
      </ol>

      <h3 className="text-lg font-medium text-purple-600 mt-6 mb-3">10.3 Sponsor Ready Report</h3>
      <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
        <p className="text-green-800 text-sm">
          <strong>Purpose:</strong> Generate comprehensive progress reports for sponsors, funding partners, and institutional stakeholders to demonstrate client business development outcomes.
        </p>
      </div>
      
      <h4 className="font-semibold text-[#333333] mt-4 mb-2">When to Generate</h4>
      <ul className="list-disc list-inside space-y-2 text-neutral-700 ml-4">
        <li><strong>At program milestones</strong> - 30, 60, 90 days into engagement</li>
        <li><strong>For sponsor reporting</strong> - When sponsors request progress updates</li>
        <li><strong>Grant reporting</strong> - To demonstrate outcomes for grant-funded programs</li>
        <li><strong>Program completion</strong> - Final report at end of engagement period</li>
        <li><strong>Funding applications</strong> - To support client's funding/loan applications</li>
      </ul>

      <h4 className="font-semibold text-[#333333] mt-4 mb-2">How to Generate</h4>
      <ol className="list-decimal list-inside space-y-2 text-neutral-700">
        <li>Navigate to your <strong>Client List</strong> in the dashboard</li>
        <li>Find the client you want to generate a report for</li>
        <li>Click the green <strong>"Report"</strong> button on the client's row</li>
        <li>Wait for the PDF to generate (loading spinner will appear)</li>
        <li>The PDF will automatically download to your computer</li>
        <li>The filename format is: <code>Sponsor_Report_[BusinessName]_[Date].pdf</code></li>
      </ol>

      <h4 className="font-semibold text-[#333333] mt-4 mb-2">Report Contents (13 Sections)</h4>
      <div className="grid md:grid-cols-2 gap-2 mt-3">
        {[
          "1. Report Overview",
          "2. Client & Engagement Details",
          "3. Program Model Description",
          "4. Baseline Business Assessment",
          "5. Business Readiness Progress",
          "6. Strategic Planning Outputs",
          "7. Operational Systems & SOPs",
          "8. Time & Capacity Optimization",
          "9. Financial & Capital Readiness",
          "10. Execution & Engagement Indicators",
          "11. Risk Reduction Summary",
          "12. Sponsor-Relevant Outcomes",
          "13. Closing Statement"
        ].map((section, idx) => (
          <div key={idx} className="flex items-center p-2 bg-neutral-50 rounded text-sm">
            <CheckCircle className="w-4 h-4 mr-2 text-green-500 flex-shrink-0" />
            <span className="text-neutral-700">{section}</span>
          </div>
        ))}
      </div>

      <h4 className="font-semibold text-[#333333] mt-4 mb-2">Data Sources</h4>
      <p className="text-neutral-700 text-sm mb-2">The report automatically pulls data from:</p>
      <ul className="list-disc list-inside space-y-1 text-neutral-600 text-sm ml-4">
        <li>Client profile and intake form</li>
        <li>Business Clarity Assessment scores</li>
        <li>SOPs created in the platform</li>
        <li>Business plan completion status</li>
        <li>Time budget analysis</li>
        <li>Completed consultant sessions</li>
        <li>Progress notes and interactions</li>
      </ul>

      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mt-4">
        <p className="text-amber-800 text-sm">
          <strong>Tip:</strong> For the most comprehensive report, ensure the client has completed their intake form, business clarity assessment, and has documented activity in the platform before generating the sponsor report.
        </p>
      </div>
    </section>
  </div>
);

export const HelpPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isConsultant = user?.role === "consultant";

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-50 via-white to-neutral-100" data-testid="help-page">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-sm border-b border-neutral-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => navigate(isConsultant ? "/consultant" : "/dashboard")} 
                className="text-neutral-600"
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                Dashboard
              </Button>
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-br from-[#333333] to-[#555555] rounded-lg flex items-center justify-center">
                  <HelpCircle className="w-5 h-5 text-[#39FF14]" />
                </div>
                <div>
                  <h1 className="text-xl font-semibold text-[#333333]">Help & Documentation</h1>
                  <p className="text-sm text-neutral-500">Step-by-step guides and SOPs</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8">
        {isConsultant ? (
          /* Consultant View - Show Tabs */
          <Tabs defaultValue="consultant" className="space-y-6">
            <TabsList className="grid w-full max-w-md grid-cols-2">
              <TabsTrigger value="consultant" className="flex items-center">
                <Users className="w-4 h-4 mr-2" />
                Consultant Guide
              </TabsTrigger>
              <TabsTrigger value="client" className="flex items-center">
                <UserCircle className="w-4 h-4 mr-2" />
                Client Guide
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="consultant">
              <Card className="border-neutral-200">
                <CardContent className="p-6 md:p-8">
                  <ScrollArea className="h-auto">
                    <ConsultantSOPContent />
                  </ScrollArea>
                </CardContent>
              </Card>
            </TabsContent>
            
            <TabsContent value="client">
              <Card className="border-neutral-200">
                <CardContent className="p-6 md:p-8">
                  <ScrollArea className="h-auto">
                    <ClientSOPContent />
                  </ScrollArea>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        ) : (
          /* Client View - Only Show Client SOP */
          <Card className="border-neutral-200">
            <CardContent className="p-6 md:p-8">
              <ScrollArea className="h-auto">
                <ClientSOPContent />
              </ScrollArea>
            </CardContent>
          </Card>
        )}

        {/* Footer */}
        <div className="mt-8 text-center text-sm text-neutral-500">
          <p>My Biz Consulting | Business Solutions Center</p>
          <p className="mt-1">Last Updated: January 2026</p>
        </div>
      </div>
    </div>
  );
};

export default HelpPage;
